import { useCallback, useEffect, useRef } from 'react';

interface UseViewportTrackingOptions {
  onEnterViewport?: () => void;
  threshold?: number;
  rootMargin?: string;
  trackOnce?: boolean;
}

export const useViewportTracking = <T extends HTMLElement = HTMLElement>({
  onEnterViewport,
  threshold = 0.5,
  rootMargin = '0px',
  trackOnce = true,
}: UseViewportTrackingOptions = {}) => {
  const elementRef = useRef<T>(null);
  const hasTracked = useRef(false);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;

      if (entry.isIntersecting && onEnterViewport) {
        if (trackOnce && hasTracked.current) {
          return;
        }

        onEnterViewport();
        hasTracked.current = true;
      }
    },
    [onEnterViewport, trackOnce],
  );

  useEffect(() => {
    const element = elementRef.current;
    if (!element || !onEnterViewport) return;

    const observer = new IntersectionObserver(handleIntersection, {
      threshold,
      rootMargin,
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [handleIntersection, threshold, rootMargin, onEnterViewport]);

  return {
    elementRef,
  };
};
